import { Injectable } from '@angular/core'
import { Observable, map, catchError, of } from 'rxjs'
import { GetCustomLabelsGQL } from '../../../_generated/graphql'

export const LABEL_KEYS = {
  OVERALL_SCORE: 'OVERALL_SCORE',
  SALES_PERSON: 'SALES_PERSON',
  CUSTOMER: 'CUSTOMER',
} as const

export type LabelKey = (typeof LABEL_KEYS)[keyof typeof LABEL_KEYS]

export type CustomLabelValueMap = Partial<Record<LabelKey, string>>

export interface CustomLabel {
  labelKey: string
  value: string
}

/**
 * CustomLabel 配列を labelKey で参照しやすい Map オブジェクトに変換する。
 * 未知の labelKey は backend 側の将来追加値として保持せず、client が知るキーだけを扱う。
 */
export function toCustomLabelValueMap(
  labels: readonly CustomLabel[],
): CustomLabelValueMap {
  const result: CustomLabelValueMap = {}
  for (const label of labels) {
    if (isLabelKey(label.labelKey)) {
      result[label.labelKey] = label.value
    }
  }
  return result
}

/**
 * Assessment 表示用ラベルを抽出する。
 * OVERALL_SCORE は見出し表示用、SALES_PERSON / CUSTOMER は話者表示用として分離する。
 */
export function extractAssessmentLabels(labels: readonly CustomLabel[]): {
  overallScoreLabel: string
  speakerLabelValues: CustomLabelValueMap
} {
  const labelValues = toCustomLabelValueMap(labels)
  const speakerLabelValues: CustomLabelValueMap = {}
  const salesPersonLabel = labelValues[LABEL_KEYS.SALES_PERSON]
  const customerLabel = labelValues[LABEL_KEYS.CUSTOMER]

  if (salesPersonLabel != null) {
    speakerLabelValues[LABEL_KEYS.SALES_PERSON] = salesPersonLabel
  }
  if (customerLabel != null) {
    speakerLabelValues[LABEL_KEYS.CUSTOMER] = customerLabel
  }

  return {
    overallScoreLabel: labelValues[LABEL_KEYS.OVERALL_SCORE] ?? '',
    speakerLabelValues,
  }
}

/**
 * 既定話者名を表示用カスタムラベルに置換する。
 * batch/src/template/const.ts の REPLACE_NAME が生成する「営業パーソンN / 顧客N」と、
 * batch/stt/merge.py の ALLOWED_ROLES が生成する「営業担当 / 顧客」の 2 系統の既定ラベルを契約として扱う
 * （連番 N は半角数字前提。全角数字はパターン不一致となり原文パススルーされる）。
 * カスタム設定がある場合のみ置換し、未設定・空文字の場合は入力値をそのまま返す
 * （空文字は通常経路では保存不可だが、直接データ操作で混入しても話者名が消えないよう防御する）。
 * 既定パターンに一致しない入力は、過去データや想定外形式を壊さないため入力値をそのまま返す。
 */
export function resolveSpeakerLabel(
  speaker: string,
  labelValues: CustomLabelValueMap,
): string {
  const salesPersonMatch = speaker.match(/^(営業パーソン|営業担当)(\d*)$/)
  if (salesPersonMatch) {
    const label = labelValues[LABEL_KEYS.SALES_PERSON]
    return !label ? speaker : `${label}${salesPersonMatch[2]}`
  }

  const customerMatch = speaker.match(/^顧客(\d*)$/)
  if (customerMatch) {
    const label = labelValues[LABEL_KEYS.CUSTOMER]
    return !label ? speaker : `${label}${customerMatch[1]}`
  }

  return speaker
}

/** client が認識している CustomLabel のキーかどうかを判定する。 */
function isLabelKey(value: string): value is LabelKey {
  return Object.values(LABEL_KEYS).includes(value as LabelKey)
}

@Injectable({
  providedIn: 'root',
})
export class CustomLabelService {
  constructor(private getCustomLabelsGQL: GetCustomLabelsGQL) {}

  /**
   * カスタムラベルを取得
   *
   * 解決優先度: SkillMap 単位 override > org 単位 > デフォルト値（backend 側で決定）
   * デフォルト値を持たないキーは backend のレスポンスから省略される。
   *
   * @param labelKeys 取得したいラベルキーの配列
   * @param skillMapId 指定時のみ SkillMap 単位 override を優先する
   */
  getCustomLabels(
    labelKeys: LabelKey[],
    skillMapId?: string,
  ): Observable<CustomLabel[]> {
    return this.getCustomLabelsGQL
      .fetch({
        variables: { labelKeys, skillMapId },
        fetchPolicy: 'cache-first',
      })
      .pipe(
        map((res) => {
          return res.data?.getCustomLabels ?? []
        }),
        catchError((err) => {
          console.error('getCustomLabels failed', err)
          return of([])
        }),
      )
  }

  /**
   * 単一のカスタムラベル値を取得
   * @param labelKey 取得したいラベルキー
   * @param skillMapId 指定時のみ SkillMap 単位 override を優先する
   */
  getLabel(labelKey: LabelKey, skillMapId?: string): Observable<string> {
    return this.getCustomLabels([labelKey], skillMapId).pipe(
      map((labels) => labels.find((l) => l.labelKey === labelKey)?.value ?? ''),
    )
  }
}
